import { useState } from "react";
import { Button } from "@/project/karuna/components/ui/button"; // @ts-ignore - button is not a valid module
import { Input } from "@/project/karuna/components/ui/input"; // @ts-ignore - input is not a valid module
import { Label } from "@/project/karuna/components/ui/label"; // @ts-ignore - label is not a valid module
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "@/project/karuna/components/ui/card"; // @ts-ignore - card is not a valid module
import { useAuth } from "@/project/karuna/contexts/AuthContext"; // @ts-ignore - AuthContext is not a valid module
import { useNavigate } from "react-router-dom";
import { GraduationCap, InfoIcon } from "lucide-react";
import { useToast } from "@/project/karuna/hooks/use-toast"; // @ts-ignore - use-toast is not a valid module
import { supabase } from "@/project/karuna/integrations/supabase/client"; // @ts-ignore - supabase is not a valid module
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from "@/project/karuna/components/ui/tooltip"; // @ts-ignore - tooltip is not a valid module
import { useKarunaLink } from "@/project/karuna/utils/navigationRoutes";

const SchoolOnboarding = () => {
  const navigate = useNavigate();
  const { toast } = useToast();
  const { user } = useAuth();
  const getLink = useKarunaLink();
  const [step, setStep] = useState(1);
  const [loading, setLoading] = useState(false);

  const [schoolData, setSchoolData] = useState({
    kcNo: "",
    schoolName: "",
    principalName: "",
    contactNumber: "",
    email: "",
    kendraName: "",
  });

  const [teacherData, setTeacherData] = useState({
    name: "",
    mobile: "",
    email: "",
  });

  const handleSchoolSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setLoading(true);

    try {
      const { error } = await supabase.from("schools").insert({
        user_id: user?.id ?? "",
        kc_no: schoolData.kcNo,
        school_name: schoolData.schoolName,
        principal_name: schoolData.principalName,
        contact_number: schoolData.contactNumber,
        email: schoolData.email,
        kendra_name: schoolData.kendraName,
        status: "approved",
        onboarding_completed: false,
      });

      if (error) {
        throw error;
      }

      toast({
        title: "School Details Saved",
        description: "Now let&apos;s add teacher details.",
      });
      setStep(2);
    } catch (error: any) {
      toast({
        title: "Error",
        description: error.message,
        variant: "destructive",
      });
    } finally {
      setLoading(false);
    }
  };

  const handleTeacherSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setLoading(true);

    try {
      const { data: school } = await supabase
        .from("schools")
        .select("id")
        .eq("user_id", user?.id ?? "")
        .single();

      if (!school) {
        throw new Error("School not found");
      }

      const { error: teacherError } = await supabase.from("teachers").insert({
        school_id: school.id,
        name: teacherData.name,
        mobile: teacherData.mobile,
        email: teacherData.email,
        academic_year: new Date().getFullYear().toString(),
      });

      if (teacherError) {
        throw teacherError;
      }

      const { error: updateError } = await supabase
        .from("schools")
        .update({ onboarding_completed: true })
        .eq("id", school.id);

      if (updateError) {
        throw updateError;
      }

      const { error: roleError } = await supabase.from("user_roles").insert({
        user_id: user?.id ?? "",
        role: "school_admin",
      });

      if (roleError && !roleError.message.includes("duplicate")) {
        throw roleError;
      }

      toast({
        title: "Onboarding Complete",
        description: "Welcome to Karuna Platform!",
      });
      navigate(getLink("/school/dashboard"));
    } catch (error: any) {
      toast({
        title: "Error",
        description: error.message,
        variant: "destructive",
      });
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="flex min-h-screen items-center justify-center bg-gradient-to-br from-background via-muted/20 to-background px-4 py-8">
      <Card className="w-full max-w-2xl border-border shadow-strong">
        <CardHeader className="space-y-4 text-center">
          <div className="mx-auto flex h-16 w-16 items-center justify-center rounded-2xl bg-gradient-hero">
            <GraduationCap className="h-10 w-10 text-primary-foreground" />
          </div>
          <div>
            <CardTitle className="text-2xl">
              {step === 1 ? "School Details" : "Teacher In-Charge Details"}
            </CardTitle>
            <CardDescription className="mt-2 text-base">
              Step {step} of 2
            </CardDescription>
          </div>
        </CardHeader>
        <CardContent>
          {step === 1 ? (
            <form onSubmit={handleSchoolSubmit} className="space-y-4">
              <div className="space-y-2">
                <div className="flex items-center gap-2">
                  <Label htmlFor="kcNo">KC No *</Label>
                  <TooltipProvider>
                    <Tooltip>
                      <TooltipTrigger asChild>
                        <InfoIcon className="h-4 w-4 cursor-help text-muted-foreground" />
                      </TooltipTrigger>
                      <TooltipContent>
                        <p>For help, call Mr. Elango at 63749 82288</p>
                      </TooltipContent>
                    </Tooltip>
                  </TooltipProvider>
                </div>
                <Input
                  id="kcNo"
                  value={schoolData.kcNo}
                  onChange={e =>
                    setSchoolData({ ...schoolData, kcNo: e.target.value })
                  }
                  required
                />
              </div>

              <div className="space-y-2">
                <Label htmlFor="schoolName">School Name *</Label>
                <Input
                  id="schoolName"
                  value={schoolData.schoolName}
                  onChange={e =>
                    setSchoolData({ ...schoolData, schoolName: e.target.value })
                  }
                  required
                />
              </div>

              <div className="space-y-2">
                <Label htmlFor="principalName">Principal&apos;s Name *</Label>
                <Input
                  id="principalName"
                  value={schoolData.principalName}
                  onChange={e =>
                    setSchoolData({
                      ...schoolData,
                      principalName: e.target.value,
                    })
                  }
                  required
                />
              </div>

              <div className="space-y-2">
                <Label htmlFor="contactNumber">Contact Number *</Label>
                <Input
                  id="contactNumber"
                  type="tel"
                  value={schoolData.contactNumber}
                  onChange={e =>
                    setSchoolData({
                      ...schoolData,
                      contactNumber: e.target.value,
                    })
                  }
                  required
                />
              </div>

              <div className="space-y-2">
                <Label htmlFor="schoolEmail">Email ID *</Label>
                <Input
                  id="schoolEmail"
                  type="email"
                  value={schoolData.email}
                  onChange={e =>
                    setSchoolData({ ...schoolData, email: e.target.value })
                  }
                  required
                />
              </div>

              <div className="space-y-2">
                <Label htmlFor="kendraName">Nearest Karuna Kendra Name *</Label>
                <Input
                  id="kendraName"
                  value={schoolData.kendraName}
                  onChange={e =>
                    setSchoolData({ ...schoolData, kendraName: e.target.value })
                  }
                  required
                />
              </div>

              <Button
                type="submit"
                className="w-full border-0 bg-gradient-hero"
                disabled={loading}
              >
                {loading ? "Saving..." : "Save & Continue"}
              </Button>
            </form>
          ) : (
            <form onSubmit={handleTeacherSubmit} className="space-y-4">
              <div className="space-y-2">
                <Label htmlFor="teacherName">Name *</Label>
                <Input
                  id="teacherName"
                  value={teacherData.name}
                  onChange={e =>
                    setTeacherData({ ...teacherData, name: e.target.value })
                  }
                  required
                />
              </div>

              <div className="space-y-2">
                <Label htmlFor="teacherMobile">Mobile Number *</Label>
                <Input
                  id="teacherMobile"
                  type="tel"
                  value={teacherData.mobile}
                  onChange={e =>
                    setTeacherData({ ...teacherData, mobile: e.target.value })
                  }
                  required
                />
              </div>

              <div className="space-y-2">
                <Label htmlFor="teacherEmail">Email ID *</Label>
                <Input
                  id="teacherEmail"
                  type="email"
                  value={teacherData.email}
                  onChange={e =>
                    setTeacherData({ ...teacherData, email: e.target.value })
                  }
                  required
                />
              </div>

              <div className="flex gap-2">
                <Button
                  type="button"
                  variant="outline"
                  className="w-full"
                  onClick={() => setStep(1)}
                >
                  Back
                </Button>
                <Button
                  type="submit"
                  className="w-full border-0 bg-gradient-hero"
                  disabled={loading}
                >
                  {loading ? "Saving..." : "Complete Setup"}
                </Button>
              </div>
            </form>
          )}
        </CardContent>
      </Card>
    </div>
  );
};

export default SchoolOnboarding;
